import './modals.css';

import React from 'react';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { translate } from '../../../common/language/translations';
import { DropDown } from '../controls/dropDowns/DropDown';
import { InputField } from '../controls/input/InputField';
import { TextButton } from '../controls/buttons/TextButton';
import { IconButton } from '../controls/buttons/IconButton';
import { createTeam, deleteTeam, editTeam } from '../../services/firestore/teams';
import { UserDropDown } from '../controls/dropDowns/UserDropDown';
import { StringToAnyMap } from '../../types/common';
import { PlatformFormation } from '../../types/formation';
import { getFormationConfig, getFormationOptions } from '../../utils/formationUtils';
import { isFokusUser } from '../../utils/userConfigUtils';


interface TeamModalProps {
  teams: StringToAnyMap | null;
  isAcademyTeams: boolean;
  setIsTeamModalOpen: (value: boolean) => void;
  tabIndexToEdit: number | undefined,
  setTabIndexToEdit: (tab: number | undefined) => void;
}


export const TeamModal: React.FC<TeamModalProps> = ({
  teams,
  isAcademyTeams,
  setIsTeamModalOpen,
  tabIndexToEdit,
  setTabIndexToEdit
}) => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const { openConfirmModal, openTextModal } = useOpenGlobalModal();

  const tenantSettings = useRecoilValue(tenantSettingsState);
  const tenantFormation = tenantSettings?.formation;

  const [searchString, setSearchString] = useState(
    tabIndexToEdit !== undefined && teams ? Object.values(teams)[tabIndexToEdit].name : ''
  );

  const [selectedFormation, setSelectedFormation] = useState<PlatformFormation[]>(
    tabIndexToEdit !== undefined && teams ? [Object.values(teams)[tabIndexToEdit].formation] : tenantFormation ? [tenantFormation] : []
  );

  const currentTeamOwner = tabIndexToEdit !== undefined && teams ? Object.values(teams)[tabIndexToEdit].teamOwner : undefined;
  const [selectedTeamOwner, setSelectedTeamOwner] = useState<string[]>(
    currentTeamOwner ? [currentTeamOwner] : []
  );

  const [isFormationDropDownExpanded, setIsFormationDropDownExpanded] = useState(false);
  const [isUserDropDownExpanded, setIsUserDropDownExpanded] = useState(false);

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(false);


  const onKeyDownSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isButtonDisabled()) {
      handleAddOrEditClick();
    }
  };

  const onChangeSearchField = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length < 26) {
      setSearchString(event.target.value);
    }
  };

  const handleAddOrEditClick = async () => {
    if (!userConfig || submitButtonDisabled || !teams || !tenantFormation) return;

    setSubmitButtonDisabled(true);

    if (tabIndexToEdit !== undefined) {
      const team = Object.values(teams)[tabIndexToEdit];
      await editTeam(
        Object.keys(teams)[tabIndexToEdit],
        team,
        getFormationConfig(team.formation, tenantSettings?.formationConfigs),
        searchString,
        selectedFormation[0],
        getFormationConfig(selectedFormation[0], tenantSettings?.formationConfigs),
        selectedTeamOwner.length > 0 ? selectedTeamOwner[0] : null,
        userConfig.email,
        userConfig.club,
        currentUser,
      );

      setIsTeamModalOpen(false);
      setTimeout(() => {
        setTabIndexToEdit(undefined);
      }, 150);
    }
    else {
      const newTeamId = await createTeam(
        searchString,
        selectedFormation[0],
        isAcademyTeams,
        userConfig?.accessConfig.logisticTeamAccess === 'personal' ? userConfig.email : selectedTeamOwner.length > 0 ? selectedTeamOwner[0] : null,
        userConfig.email,
        userConfig.club,
        currentUser,
      );

      if (newTeamId !== undefined) {
        setIsTeamModalOpen(false);
      }
    }

    setTimeout(() => {
      setSubmitButtonDisabled(false);
    }, 500);
  };


  const confirmDelete = async () => {
    if (!userConfig || tabIndexToEdit === undefined || !teams || deleteButtonDisabled) return;

    setDeleteButtonDisabled(true);

    setIsTeamModalOpen(false);

    const teamId = Object.keys(teams)[tabIndexToEdit];
    const team = Object.values(teams)[tabIndexToEdit];

    setTabIndexToEdit(undefined);

    await deleteTeam(
      teamId,
      team,
      userConfig.email,
      userConfig.club,
      currentUser,
    );

    setTimeout(() => {
      setDeleteButtonDisabled(false);
    }, 500);
  };


  const removeDropDownExpansions = () => {
    setIsFormationDropDownExpanded(false);
    setIsUserDropDownExpanded(false);
  };


  const isButtonDisabled = () => {
    if (submitButtonDisabled) return true;

    if (searchString.length === 0) {
      return true;
    }

    if (tabIndexToEdit === undefined) {
      return searchString.length === 0 || selectedFormation.length === 0;
    }

    const isNewName = teams && searchString.length > 0 && searchString !== Object.values(teams)[tabIndexToEdit].name;
    const isNewFormation = teams && selectedFormation.length > 0 && selectedFormation[0] !== Object.values(teams)[tabIndexToEdit].formation;
    const isNewUser = (selectedTeamOwner.length > 0 && selectedTeamOwner[0] !== currentTeamOwner)
      || (selectedTeamOwner.length === 0 && currentTeamOwner);

    return !isNewName && !isNewFormation && !isNewUser;
  };


  const getButtonText = () => {
    if (!userConfig) return '';

    if (tabIndexToEdit === undefined) {
      return translate('createTeam', userConfig.language);
    }

    return translate('confirmEdits', userConfig.language);
  };


  // valid options are users with limited scout access + current team owner, if any
  const userDropDownOptions = useMemo(() => {
    let options = Object.values(allUsersWithSameTenant).filter(user => user.accessConfig.logisticTeamAccess === 'personal');

    // if the user is not a fokus user, we remove all fokus users as options
    if (!isFokusUser(userConfig)) {
      options = options.filter(user => !isFokusUser(user));
    }

    if (currentTeamOwner && allUsersWithSameTenant[currentTeamOwner] && !options.some(user => user.email === currentTeamOwner)) {
      options.push(allUsersWithSameTenant[currentTeamOwner]);
    }

    return options;
  }, [allUsersWithSameTenant, currentTeamOwner, userConfig]);


  const showUserDropDown = !isAcademyTeams && userConfig?.accessConfig.logisticTeamAccess === 'full' && userDropDownOptions.length > 0;


  return (
    <div className='modal-root-container team-modal-root'>

      {(isFormationDropDownExpanded || isUserDropDownExpanded) &&
        <div className='modal-empty-background' onClick={() => removeDropDownExpansions()} />
      }

      <IconButton
        icon={<CloseIcon style={{ fontSize: 24 }} />}
        onClick={() => setIsTeamModalOpen(false)}
        size={30}
        style={{ position: 'absolute', top: 12, right: 12 }}
        startWhite={true}
      />

      {!deleteButtonDisabled && (
        <div className='modal-root-title'>
          {tabIndexToEdit !== undefined && teams
            ? Object.values(teams)[tabIndexToEdit].name
            : translate('addNewTeam', userConfig?.language)
          }
        </div>
      )}

      <div className='modal-divider modal-root-title-divider' />

      {!deleteButtonDisabled && (
        <div className='modal-content-container'>

          <div
            className='modal-content-section'
            style={{ minHeight: 250 }}
          >
            <div className='modal-section-title'>
              {tabIndexToEdit !== undefined && translate('editTeamName', userConfig?.language)}
              {tabIndexToEdit === undefined && translate('provideTeamName', userConfig?.language)}
            </div>

            <InputField
              value={searchString}
              placeholder={translate('provideName', userConfig?.language)}
              onChange={onChangeSearchField}
              onKeyDown={onKeyDownSearchField}
              resetValue={() => setSearchString('')}
              width={240}
            />

            <div className='modal-divider' style={{ marginTop: 15 }} />

            <div className='modal-section-title'>
              {translate(tabIndexToEdit !== undefined ? 'editFormation' : 'selectFormation', userConfig?.language)}
            </div>

            <div className='team-modal-drop-down' style={{ width: showUserDropDown ? 300 : undefined }}>
              <DropDown
                id={'team-modal-formation-select'}
                dropDownOptions={getFormationOptions(tenantSettings?.formationConfigs)}
                selectedOptions={selectedFormation}
                setSelectedOptions={setSelectedFormation}
                isDropDownExpanded={isFormationDropDownExpanded}
                setIsDropDownExpanded={setIsFormationDropDownExpanded}
                defaultDropDownText={'selectFormation'}
                defaultDropDownTextColor={'#000000'}
                marginBetweenOptions={0}
                maxHeight={'50vh'}
                height={26}
              />
            </div>

            {showUserDropDown && (
              <React.Fragment>
                <div className='modal-divider' style={{ marginTop: 15 }} />

                <div className='modal-section-title flex-row align-center justify-center' style={{ gap: 5 }}>
                  {translate(currentTeamOwner ? 'editLimitedScout' : 'selectLimitedScout?', userConfig?.language)}

                  <IconButton
                    icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
                    onClick={() => openTextModal(
                      translate('limitedScoutTeams', userConfig?.language),
                      translate('limitedScoutTeamsInfo', userConfig?.language),
                      { infoType: 'infoIcon', titleKey: 'teamOwner' }
                    )}
                    size={24}
                    style={{ marginRight: -21 }}
                  />
                </div>

                <div className='team-modal-drop-down' style={{ width: 300 }}>
                  {selectedTeamOwner.length > 0 && (
                    <IconButton
                      icon={<CloseIcon style={{ fontSize: 18 }} />}
                      onClick={() => setSelectedTeamOwner([])}
                      size={24}
                      style={{ position: 'absolute', left: -36 }}
                    />
                  )}

                  <UserDropDown
                    id={'team-modal-user-select'}
                    userMap={allUsersWithSameTenant}
                    dropDownOptions={userDropDownOptions}
                    selectedOptions={selectedTeamOwner}
                    setSelectedOptions={setSelectedTeamOwner}
                    isDropDownExpanded={isUserDropDownExpanded}
                    setIsDropDownExpanded={setIsUserDropDownExpanded}
                    defaultDropDownText={translate('selectUser', userConfig?.language)}
                    defaultDropDownTextColor={'#000000'}
                    marginBetweenOptions={2}
                    maxHeight={'calc(20vh + 100px)'}
                    height={26}
                  />
                </div>
              </React.Fragment>
            )}
          </div>

          <div
            className='modal-content-section'
            style={{ maxHeight: 160, minHeight: tabIndexToEdit !== undefined ? 120 : 80 }}
          >
            <TextButton
              onClick={async () => await handleAddOrEditClick()}
              text={getButtonText()}
              icon={tabIndexToEdit === undefined ? <AddIcon style={{ fontSize: 24 }} /> : <UpdateIcon style={{ fontSize: 24 }} />}
              disabled={isButtonDisabled()}
            />

            {tabIndexToEdit !== undefined && <div className='modal-divider' />}
            {tabIndexToEdit !== undefined && (
              <TextButton
                onClick={() => openConfirmModal(confirmDelete, 'deleteTeam', 'noRegretInfo')}
                text={translate('deleteTeam', userConfig?.language)}
                icon={<DeleteOutlineIcon style={{ fontSize: 24 }} />}
              />
            )}
          </div>

        </div>
      )}

    </div>
  );
};
